/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-30 14:05:17
 *
 *******************************************************/
// @flow

import _ from 'lodash';
// import moment from 'moment';
import moment from 'moment-timezone';
import { store } from 'src/stores/store';
import { checkHasSpecialCharacters } from 'src/helpers';
import { getStoreDate, getUTCDate } from './date-time-helper';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { DATE_FORMAT } from 'src/constants/language-setting';

/**
 * Build initial data for product ranking
 *
 * @param  {Object} productRanking value from API
 *
 * @return {Object} initialValue
 */
export const buildInitialProductRanking = (productRanking: Object) => {
  if (_.isEmpty(productRanking)) {
    return [];
  }

  const result = productRanking.map((item, index) => {
    const { ranking = {} } = item;
    let enabledFromDate = _.get(ranking, 'enabled_from_date');
    enabledFromDate = enabledFromDate ? getStoreDate(enabledFromDate) : undefined;

    let enabledToDate = _.get(ranking, 'enabled_to_date');
    enabledToDate = enabledToDate ? getStoreDate(enabledToDate) : undefined;

    let keywords = _.get(ranking, 'keywords', []);
    keywords = !_.isEmpty(keywords) ? keywords.join(',') : '';

    return {
      id: item.id,
      title: item.title,
      images: item.images,
      rankingId: item.rankingSortId,
      rankingType: _.get(ranking, 'type', ''),
      keywords,
      enabledFromDate,
      enabledToDate
    };
  });
  return { productRanking: result };
};

/**
 * Build product rankings before saving
 *
 * @param  {array} productRankings list of productRanking
 *
 *  * @return {Object} save product
 */
export const buildProductRanking = (productRanking: Object) => {
  if (_.isEmpty(productRanking)) {
    return [];
  }

  const savedProductRanking = productRanking.map((item, index) => {
    const { keywords } = item;
    const saveKeywords = keywords ? keywords.split(',') : undefined;

    let ranking = {};
    if (!_.isEmpty(item.rankingType)) {
      let enabled_from_date = item.enabledFromDate ? getUTCDate(item.enabledFromDate, 0, 0, 0) : '';
      let enabled_to_date = item.enabledToDate ? getUTCDate(item.enabledToDate, 23, 59, 59) : '';

      ranking = {
        type: item.rankingType,
        keywords: saveKeywords,
        enabled_from_date,
        enabled_to_date
      };
    }

    return {
      id: item.id,
      title: item.title,
      images: item.images,
      rankingSortId: item.rankingId,
      ranking
    };
  });
  return savedProductRanking;
};

/**
 * Check validate product ranking
 *
 * @param  {array} productRankings
 * @param  {I18n} i18n
 *
 * @return {string}          value
 */
export const checkValidateProductRanking = (productRanking: Object, i18n: Object) => {
  for (let item of productRanking) {
    // Search term
    if (item.rankingType === 'keywordSearch') {
      // if (item.keywords.length > 128) {
      //   return 'Keywords must not exceed 128 characters';
      // }

      let checkSpecialCharactersValue = checkHasSpecialCharacters(item.keywords);
      if (_.isEmpty(item.keywords.trim()) || checkSpecialCharactersValue) {
        return i18n.translate('AdminHTML.ProductRanking.Helper.checkValidateProductRanking_Error');
      }
    }
  }
  return '';
};

/**
 * Build dates from the date range
 *
 * @param  {string} selectedDateRange the selected range
 *
 *  * @return {Object} save product
 */
export const buildDateFromDateRange = (selectedDateRange: string) => {
  // Format date with language
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDateNumber = DATE_FORMAT[appLanguage].number;

  let startDate, endDate, selectedDates;
  const today = getStoreDate(new Date());
  switch (selectedDateRange) {
    // None: no limited time
    case 'none': {
      startDate = '';
      endDate = '';
      selectedDates = {
        start: today,
        end: today
      };
      break;
    }
    // today: only in today
    case 'today': {
      startDate = moment(today).format(formatDateNumber);
      endDate = moment(today).format(formatDateNumber);
      selectedDates = {
        start: today,
        end: today
      };
      break;
    }
    // tomorrow: only in tomorrow
    case 'tomorrow': {
      const tomorrow = moment(today).add(1, 'days');

      startDate = tomorrow.format(formatDateNumber);
      endDate = tomorrow.format(formatDateNumber);
      selectedDates = {
        start: tomorrow.toDate(),
        end: tomorrow.toDate()
      };
      break;
    }
    // next week: from the first day to the last date of next week
    case 'nextWeek': {
      const nextWeekStart = moment().add(1, 'weeks').startOf('weeks');
      const nextWeekEnd = moment().add(1, 'weeks').endOf('weeks');

      startDate = nextWeekStart.format(formatDateNumber);
      endDate = nextWeekEnd.format(formatDateNumber);
      selectedDates = {
        start: nextWeekStart.toDate(),
        end: nextWeekEnd.toDate()
      };
      break;
    }
    // next month: from the first to the end of next month
    case 'nextMonth': {
      const nextMonthStart = moment().add(1, 'months').startOf('month');
      const nextMonthEnd = moment().add(1, 'months').endOf('month');

      startDate = nextMonthStart.format(formatDateNumber);
      endDate = nextMonthEnd.format(formatDateNumber);
      selectedDates = {
        start: nextMonthStart.toDate(),
        end: nextMonthEnd.toDate()
      };
      break;
    }
    default: {
      //custom
      startDate = moment(today).format(formatDateNumber);
      endDate = moment(today).format(formatDateNumber);
      selectedDates = {
        start: today,
        end: today
      };
      break;
    }
  }

  return {
    startDate,
    endDate,
    selectedDates
  };
};

/**
 * Build date range from dates
 *
 * @param  {date} fromDate
 * @param  {date} toDAte
 *
 *  * @return {Object} date range name
 */
export const buildDateRangeFromDate = (fromDate: Date, toDate: Date) => {
  const today = getStoreDate(new Date());
  let result = 'custom';

  if (moment(fromDate).isSame(today, 'day') && moment(toDate).isSame(today, 'day')) {
    result = 'today';
  } else {
    const tomorrow = moment(today).add(1, 'days');
    if (moment(fromDate).isSame(tomorrow, 'day') && moment(toDate).isSame(tomorrow, 'day')) {
      result = 'tomorrow';
    } else {
      const nextWeekStart = moment().add(1, 'weeks').startOf('weeks');
      const nextWeekEnd = moment().add(1, 'weeks').endOf('weeks');
      if (
        moment(fromDate).isSame(nextWeekStart, 'day') &&
        moment(toDate).isSame(nextWeekEnd, 'day')
      ) {
        result = 'nextWeek';
      } else {
        const nextMonthStart = moment().add(1, 'months').startOf('month');
        const nextMonthEnd = moment().add(1, 'months').endOf('month');
        if (
          moment(fromDate).isSame(nextMonthStart, 'day') &&
          moment(toDate).isSame(nextMonthEnd, 'day')
        ) {
          result = 'nextMonth';
        } else {
          const last90Day = moment(today).add(-90, 'days');
          if (moment(fromDate).isSame(last90Day, 'day')) {
            result = 'last90Days';
          }
        }
      }
    }
  }

  return result;
};
